// The filter icon beside a screen's search box, and the panel it opens.
//
// The menu knows nothing about what is filtered. A screen hands it the
// sections to show in the panel, the filters that are on, and how to clear
// them all; each filter keeps its own state, usually in the page address. Action
// Owner is the first, and the menu is shaped so that it need not be the last.
//
// A FILTER THAT IS ON STAYS VISIBLE WITH THE PANEL SHUT. The icon fills, and
// each filter that is on gets a tag beside it naming what it is narrowing to,
// with a button to take it off. A grid narrowed to somebody looks the same as
// one that is not, and a filter nobody can see is one nobody remembers setting.
//
// THE PANEL IS A NON-MODAL DIALOG, NOT AN ARIA MENU. A menu may hold only menu
// items, and a section can hold a field -- the Action Owner people picker is a
// combobox. Escape and a click outside close it, and so does a choice, through
// the `close` each section is given. Focus goes back to the icon, except after
// a click outside, which has put it wherever the click landed.
//
// Opening it puts focus on whatever a section has marked `data-autofocus` --
// Action Owner's Apply, while it offers the signed-in user -- or else on the
// first field, or else on the first button.

import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Funnel } from 'lucide-react';

export interface ActiveFilter {
  key: string;
  /** What the filter narrows to, as its tag shows it. */
  label: string;
  onClear: () => void;
}

export interface FilterMenuProps {
  active: ActiveFilter[];
  /** Clears every filter at once. A screen does this in one step rather than
   *  clearing each in turn, because each clear would otherwise start from an
   *  address the one before had not yet changed. */
  onClearAll: () => void;
  /** The panel's sections, given a `close` for once a choice is made. */
  children: (close: () => void) => ReactNode;
}

export function FilterMenu({ active, onClearAll, children }: FilterMenuProps) {
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const on = active.length > 0;

  function close() {
    setOpen(false);
    button.current?.focus();
  }

  useEffect(() => {
    if (!open) return;
    const within = panel.current;
    (
      within?.querySelector<HTMLElement>('[data-autofocus]') ??
      within?.querySelector<HTMLElement>('input') ??
      within?.querySelector<HTMLElement>('button:not(:disabled)')
    )?.focus();
    const outside = (event: MouseEvent) => {
      if (!anchor.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', outside);
    return () => document.removeEventListener('mousedown', outside);
  }, [open]);

  return (
    <div className="filter-menu">
      <div className="filter-anchor" ref={anchor}>
        <button
          type="button"
          ref={button}
          className={on ? 'filter-button on' : 'filter-button'}
          aria-label="Filter"
          title="Filter"
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          onClick={() => setOpen((was) => !was)}
        >
          <Funnel size={18} aria-hidden fill={on ? 'currentColor' : 'none'} />
        </button>

        {open && (
          <div
            className="filter-panel"
            id={panelId}
            ref={panel}
            role="dialog"
            aria-label="Filter"
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                close();
              }
            }}
          >
            {children(close)}
            <div className="filter-panel-foot">
              <button
                type="button"
                className="link"
                disabled={!on}
                onClick={() => {
                  onClearAll();
                  close();
                }}
              >
                Clear filter
              </button>
            </div>
          </div>
        )}
      </div>

      {on && (
        <ul className="filter-tags" aria-label="Filters on">
          {active.map((filter) => (
            <li key={filter.key} className="filter-tag">
              <span>{filter.label}</span>
              <button type="button" aria-label={`Remove filter ${filter.label}`} onClick={filter.onClear}>
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
